import { ref, computed } from 'vue';
import { chartTheme } from '@rdrive/theme';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';

// The pin and frame colours — @rdrive/theme's `chart.pin`, the same in both modes (standalone
// AM's MiniMap literals, tokenised at AM_ABSORPTION_PLAN step B3). Handed to ./style.css as
// custom properties; it holds no colour literal.
const PIN = chartTheme().pin;
const PIN_VARS = Object.freeze({
    '--rdrive-pin-fill': PIN.fill,
    '--rdrive-pin-border': PIN.border,
    '--rdrive-pin-shadow': PIN.shadow,
    '--rdrive-pin-frame-bg': PIN.frameBackground,
    '--rdrive-pin-frame-border': PIN.frameBorder
});

export default (props) => {
    // LOCAL composer with the warn flags off (@rdrive/i18n): viewDrawing, miniMap, close.
    const { t } = useComponentI18n(messages);

    const showDialog = ref(false);
    // Natural size of the drawing image, needed to place pixel coordinates.
    const naturalSize = ref(null);

    const hasPin = computed(() => Number.isFinite(props.x) && Number.isFinite(props.y));

    const pinStyle = computed(() => {
        if (props.pinUnits === 'pixels') {
            // Without the image's natural size the fraction is unknowable, so hold
            // the pin centred rather than placing it wrongly.
            if (!naturalSize.value) return { left: '50%', top: '50%', opacity: 0 };
            return {
                left: `${((props.x ?? 0) / naturalSize.value.width) * 100}%`,
                top: `${((props.y ?? 0) / naturalSize.value.height) * 100}%`
            };
        }
        return {
            left: `${(props.x ?? 0) * 100}%`,
            top: `${(props.y ?? 0) * 100}%`
        };
    });

    // Both images report here (as in AM): the last one to load sets the size pixel pins use.
    const handleImageLoad = (event) => {
        const image = event.target;
        if (image.naturalWidth && image.naturalHeight) {
            naturalSize.value = { width: image.naturalWidth, height: image.naturalHeight };
        }
    };

    const tooltipText = computed(() =>
        [props.drawingTitle, props.locationTitle].filter(Boolean).join(' - ') || t('viewDrawing')
    );

    // The thumbnail falls back to the full drawing (AM rendered an <img> without a src when a
    // drawing had a URL but no thumbnail — its grid showed the map for either)
    const thumbnailSrc = computed(() => props.thumbnailUrl || props.drawingUrl);

    return {
        t,
        pinVars: PIN_VARS,
        showDialog,
        hasPin,
        pinStyle,
        handleImageLoad,
        tooltipText,
        thumbnailSrc
    };
};
